import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { MembersFileRecord } from '../interfaces/members-file-record';
import { MembersTableColumnErrors } from '../types/members-table-column-errors';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { DateUtils } from '../../../../../utils/date-utils';
import { SinUtils } from '../../../../../utils/sin-utils';
import { MembersFileTableRow } from '../interfaces/members-file-table-row';
import { Subscribable } from '../../../../../models/base/subscribable';

@Component({
  selector: 'app-batch-add-member-file-preview-table',
  templateUrl: './batch-add-member-file-preview-table.component.html',
  styleUrls: ['./batch-add-member-file-preview-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class BatchAddMemberFilePreviewTableComponent extends Subscribable implements OnChanges {
  @Input() records: MembersFileRecord[] = [];
  @Output() invalidRows: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() invalidColumns: EventEmitter<MembersTableColumnErrors> = new EventEmitter<MembersTableColumnErrors>();

  private _allRecords = new BehaviorSubject<MembersFileRecord[]>([]);
  // Preprocessing the records to convert date and SIN to valid values
  public processedRecords$ = this._allRecords.pipe(
    map(records =>
      records.map((record, i) => ({
        ...record,
        ROW_NUMBER: i + 1,
        DATE_OF_BIRTH: this.parseJSONDate(record['DATE OF BIRTH']?.toString()),
        EMPLOYMENT_START: this.parseJSONDate(record['EMPLOYMENT START']?.toString()),
        SIN: this.validateSin(record.SIN)
      }))
    )
  );

  public columns = [
    $localize`LAST NAME`,
    $localize`FIRST NAME`,
    $localize`DATE_OF_BIRTH`,
    $localize`SIN`,
    $localize`EMAIL`,
    $localize`EMPLOYMENT START`
  ];

  public columnErrors$ = this.processedRecords$.pipe(
    map(records => {
      const columnErrors: MembersTableColumnErrors = {};
      const invalidRecords: MembersFileTableRow[] = [];

      this.columns.forEach(column => {
        columnErrors[column] = { hasError: false, rowsWithErrors: [] };
      });

      records.forEach((record, index) => {
        let recordIsInvalid = false;

        this.columns.forEach(column => {
          // @ts-ignore
          const columnHasError = !(column in record) || !record[column as keyof TableRow];
          if (columnHasError) {
            columnErrors[column].hasError = true;
            columnErrors[column].rowsWithErrors.push(index + 1);
            recordIsInvalid = true;
          }
        });

        if (recordIsInvalid) {
          // @ts-ignore
          invalidRecords.push(record);
        }
      });

      return { columnErrors, invalidRecords };
    })
  );

  public recordsToDisplay$ = this.processedRecords$.pipe(
    map(records => {
      return records.slice(0, 5);
    })
  );

  public listenToColumnErrors = this.columnErrors$.subscribeWhileAlive({
    owner: this,
    next: ({ columnErrors, invalidRecords }) => {
      const invalidCols: MembersTableColumnErrors = {};
      for (const key in columnErrors) {
        if (columnErrors[key].hasError) {
          invalidCols[key] = columnErrors[key];
        }
      }
      this.invalidRows.emit(invalidRecords.map(record => record.ROW_NUMBER));
      this.invalidColumns.emit(invalidCols);
    }
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.records) {
      this._allRecords.next(this.records);
    }
  }

  private parseJSONDate(dateString: string): Date | null {
    if (!dateString) return null;
    const date = DateUtils.convertCSVDateStringToUTCDate(dateString);
    return isNaN(date.getTime()) ? null : date;
  }

  private validateSin(sin: number): number | null {
    const isValidSin = SinUtils.isValidSIN(sin);
    return isValidSin ? sin : null;
  }
}
